/**
 * Side-by-side view of the two stubs behind one paystub discrepancy: the app's own
 * stub on the left, the imported stub on the right, every line listed and the ones
 * that disagree highlighted — so the right one is obvious at a glance. Each column
 * header opens the full stub.
 */
import { useEffect, useState } from "react";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmt = (row, v) => (row.kind === "hours" ? (v == null ? "—" : `${Number(v).toFixed(2)} h`) : money(v));

export default function StubCompare({ discId, onOpenApp, onOpenImported, onPick }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [picking, setPicking] = useState(null); // "app" | "knit" while resolving

  useEffect(() => {
    let alive = true;
    api.get(`/payroll/discrepancies/${discId}/compare`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load the two stubs"); });
    return () => { alive = false; };
  }, [discId]);

  if (err) return <p className="mt-2 text-xs text-red-600" data-testid={`compare-error-${discId}`}>{err}</p>;
  if (!data) return <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading both stubs…</p>;

  const { app, imported, rows } = data;
  const nDiff = rows.filter((r) => r.differs).length;
  const pick = async (choice) => {
    setPicking(choice);
    try { await onPick(choice); } finally { setPicking(null); }
  };
  // Tap the column header = that stub wins (same one-tap resolve as the buttons below);
  // the small arrow beside it opens the full stub without choosing anything.
  const Head = ({ title, sub, onOpen, choice, testId, canPick }) => (
    <th className="px-2 py-1.5 text-right align-top font-normal">
      <span className="inline-flex items-center gap-1">
        <button type="button" onClick={() => pick(choice)} disabled={!canPick || picking} data-testid={testId}
          title={canPick ? `Use the ${title.toLowerCase()} — resolves this discrepancy` : `No ${title.toLowerCase()} to use`}
          className="inline-flex items-center gap-1 rounded-md border border-[#1a3fd1]/40 bg-[#1a3fd1]/5 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-[#1a3fd1] hover:bg-[#1a3fd1] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-400">
          {picking === choice ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Use {title}
        </button>
        <button type="button" onClick={onOpen} disabled={!onOpen} data-testid={`${testId}-open`} aria-label={`Open the full ${title.toLowerCase()}`} title={`Open the full ${title.toLowerCase()}`}
          className="rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-[#1a3fd1] disabled:invisible"><ExternalLink className="h-3 w-3" /></button>
      </span>
      <p className="font-mono text-[9px] normal-case tracking-normal text-gray-500">{sub}</p>
    </th>
  );

  return (
    <div className="mt-2 overflow-hidden rounded-md border border-gray-200 bg-white" data-testid={`compare-${discId}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-2 py-1.5 text-left align-top font-mono text-[9px] font-normal uppercase tracking-widest text-gray-500">
              Line<p className="normal-case tracking-normal">{nDiff} of {rows.length} differ</p>
            </th>
            <Head title="App stub" testId={`compare-pick-app-${discId}`} choice="app" canPick={Boolean(app && imported)}
              sub={app ? `${app.week_start} → ${app.week_end}${app.corrected ? " · corrected" : ""}` : "no app stub for this week"}
              onOpen={app?.paystub_id ? () => onOpenApp(app.paystub_id) : null} />
            <Head title="Imported stub" testId={`compare-pick-imported-${discId}`} choice="knit" canPick={Boolean(app && imported)}
              sub={imported ? `${imported.period_start} → ${imported.period_end} · paid ${imported.pay_date}` : "imported stub was deleted"}
              onOpen={imported?.import_id ? () => onOpenImported(imported.import_id) : null} />
            <th className="px-2 py-1.5 text-right align-top font-mono text-[9px] font-normal uppercase tracking-widest text-gray-500">Imported − app</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field} data-testid={`compare-row-${r.field}`}
              className={`border-t border-gray-100 font-mono tabular-nums ${r.differs ? "bg-amber-50 font-bold text-gray-900" : "text-gray-500"}`}>
              <td className="px-2 py-1 text-left font-sans">{r.label}</td>
              <td className="px-2 py-1 text-right">{fmt(r, r.app)}</td>
              <td className="px-2 py-1 text-right">{fmt(r, r.imported)}</td>
              <td className={`px-2 py-1 text-right ${r.differs ? (r.diff > 0 ? "text-emerald-700" : "text-red-600") : ""}`}>
                {r.differs ? `${r.diff > 0 ? "+" : ""}${fmt(r, r.diff)}` : r.diff == null ? "—" : "same"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
